import * as React from "react";
import { useQuery } from 'react-query';
import { renderRoutes } from "react-router-config";
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import { Header } from "./Header";

import { ReadAloudSource } from '../../Shared/components/readAloud/readAloudSource';
import { CLIP_SOURCE_TYPE, clipSource } from '../../Shared/libs/speech/clipSource';
import { GRAMMAR } from '../../Shared/utils/constants';
import { addKeyDownListener } from '../../Shared/hooks/addKeyDownListener';
import i18n from '../../Shared/libs/translations/i18n';
import { ReadAloudProvider, ScreenreaderInstructions, TeacherPreviewMenu, getParameterByName, getlanguageOptions, hasTeacherPreview, } from '../../Shared/index';
import { fetchUserRole } from '../../Shared/utils/userAPIs';
import { routes } from "../routes";
import { setLanguage } from "../actions/session";

export const PageLayout = ({ dispatch, grammarActivities, session, t }) => {

  const studentSession = getParameterByName('student', window.location.href);
  const proofreaderSession = getParameterByName('proofreaderSessionId', window.location.href);
  const turkSession = window.location.href.includes('turk');
  const studentOrTurkOrProofreader = studentSession || turkSession || proofreaderSession;
  const isPlaying = window.location.href.includes('play');
  const { data } = useQuery("user-role", fetchUserRole);
  const isTeacherOrAdmin = data && data.role && data.role !== 'student';
  const language = session?.language
  const languageInitializedRef = React.useRef(false);

  const [showFocusState, setShowFocusState] = React.useState<boolean>(false);
  const [previewShowing, setPreviewShowing] = React.useState<boolean>(!studentOrTurkOrProofreader || hasTeacherPreview());
  const [questionToPreview, setQuestionToPreview] = React.useState<any>(null);
  const [switchedBackToPreview, setSwitchedBackToPreview] = React.useState<boolean>(false);
  const [skippedToQuestionFromIntro, setSkippedToQuestionFromIntro] = React.useState<boolean>(false);
  const [languageOptions, setLanguageOptions] = React.useState<any>(null);

  React.useEffect(() => {
    if (!grammarActivities?.hasreceiveddata) { return }
    handleSetLanguageOptions()
  }, [grammarActivities]);

  React.useEffect(() => {
    if (languageInitializedRef.current) { return }
    if (language && i18n.language !== language) {
      handleUpdateLanguage(language)
      languageInitializedRef.current = true
    }
  }, [session]);

  function handleKeyDown (e: any) {
    if (e.key !== 'Tab') { return }
    if (showFocusState) { return }

    setShowFocusState(true);
  }

  addKeyDownListener(handleKeyDown);

  function handleSkipToMainContentClick () {
    const element = document.getElementById("main-content")
    if (!element) { return }
    element.focus()
    element.scrollIntoView()
  }

  function handleTogglePreviewMenu () {
    if(previewShowing) {
      setSwitchedBackToPreview(false);
    } else {
      setSwitchedBackToPreview(true);
    }
    setPreviewShowing(!previewShowing);
  }

  function handleToggleQuestion (question: object) {
    setQuestionToPreview(question);
  }

  function handleSkipToQuestionFromIntro () {
    setSkippedToQuestionFromIntro(true);
  }

  function handleUpdateLanguage(language: string) {
    i18n.changeLanguage(language);
    const action = setLanguage(language)
    dispatch(action)
  }

  function handleSetLanguageOptions() {
    const translations = grammarActivities.currentActivity?.translations || {};
    const formattedLanguageOptions = getlanguageOptions(translations)
    setLanguageOptions(formattedLanguageOptions);
  }

  function renderContent (showPreview: boolean, isOnMobile: boolean) {
    return(
      <main>
        <div id="main-content" tabIndex={-1}>{renderRoutes(routes, {
          isOnMobile: isOnMobile,
          handleTogglePreviewMenu: handleTogglePreviewMenu,
          switchedBackToPreview: switchedBackToPreview,
          handleToggleQuestion: handleToggleQuestion,
          previewMode: showPreview,
          questionToPreview: questionToPreview,
          skippedToQuestionFromIntro: skippedToQuestionFromIntro,
          availableLanguages: languageOptions?.map(option => option.value),
          updateLanguage: handleUpdateLanguage,
          language: language,
          translate: t
        })}</div>
      </main>
    );
  }

  const showPreview = previewShowing && (isTeacherOrAdmin || hasTeacherPreview()) && isPlaying;
  const isOnMobile = window.innerWidth < 1100;
  let className = "ant-layout ";
  className += showFocusState ? '' : 'hide-focus-outline';
  let header;

  if(isPlaying && (isTeacherOrAdmin || hasTeacherPreview())) {
    header = (
      <Header
        isOnMobile={isOnMobile}
        isTeacher={!studentOrTurkOrProofreader}
        language={language}
        languageOptions={languageOptions}
        onTogglePreview={handleTogglePreviewMenu}
        previewShowing={showPreview}
        translate={t}
        updateLanguage={handleUpdateLanguage}
      />
    );
  } else if(isPlaying) {
    header = (
      <Header
        language={language}
        languageOptions={languageOptions}
        translate={t}
        updateLanguage={handleUpdateLanguage}
      />
    );
  }

  return(
    <ReadAloudProvider activitySessionUid={studentSession || proofreaderSession} tool={GRAMMAR}>
      <ReadAloudSource source={clipSource(CLIP_SOURCE_TYPE.ACTIVITY, getParameterByName('uid', window.location.href))}>
        <div className={className}>
          <ScreenreaderInstructions />
          <button className="skip-main" onClick={handleSkipToMainContentClick} type="button">Skip to main content</button>
          {header}
          <div className="activity-container">
            {showPreview && <div className="sider-container">
              <TeacherPreviewMenu
                isOnMobile={isOnMobile}
                onHandleSkipToQuestionFromIntro={handleSkipToQuestionFromIntro}
                onTogglePreview={handleTogglePreviewMenu}
                onToggleQuestion={handleToggleQuestion}
                questionToPreview={questionToPreview}
                showPreview={previewShowing}
              />
            </div>}
            {renderContent(showPreview, isOnMobile)}
          </div>
        </div>
      </ReadAloudSource>
    </ReadAloudProvider>
  );
}

const select = (state: any, props: any) => {
  return {
    grammarActivities: state.grammarActivities,
    session: state.session
  };
}

export default withTranslation()(connect(select)(PageLayout));
